// Reusable list-page filter controls. Compose inside <FilterBar>.
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { DATE_PRESETS, datePreset, type DateRange } from '@/lib/filters';

/** A flex row that wraps filter controls with consistent spacing. */
export function FilterBar({ children }: { children: React.ReactNode }) {
  return <div className="filter-bar">{children}</div>;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="filter-field od-field">
      <span className="text-xs text-muted-foreground">{label}</span>
      {children}
    </div>
  );
}

export interface Option {
  value: string;
  label: string;
}

/** A labelled dropdown filter. Value 'all' means "no filter". */
export function SelectFilter({
  label,
  value,
  onChange,
  options,
  allLabel = 'All',
  width = 'w-44',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Option[];
  allLabel?: string;
  width?: string;
}) {
  return (
    <Field label={label}>
      <Select value={value || 'all'} onValueChange={onChange}>
        <SelectTrigger aria-label={label} className={width}>
          <SelectValue placeholder={allLabel} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{allLabel}</SelectItem>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}

/** A debounced text search box. */
export function SearchInput({
  label = 'Search',
  value,
  onChange,
  placeholder = 'Search…',
}: {
  label?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <Field label={label}>
      <Input
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-56"
      />
    </Field>
  );
}

/**
 * Date-range filter: a preset dropdown + From/To native date inputs.
 * `value.to` is treated as an exclusive end (presets already account for this);
 * when the user picks a To date manually we add a day so it reads inclusively.
 */
export function DateRangeFilter({
  label = 'Date',
  value,
  onChange,
}: {
  label?: string;
  value: DateRange;
  onChange: (r: DateRange) => void;
}) {
  const addDay = (d: string) => {
    const dt = new Date(`${d}T00:00:00Z`);
    dt.setUTCDate(dt.getUTCDate() + 1);
    return dt.toISOString().slice(0, 10);
  };
  return (
    <>
      <Field label={label}>
        <Select
          value="custom"
          onValueChange={(k) => onChange(k === 'all' ? {} : datePreset(k))}
        >
          <SelectTrigger aria-label={`${label} range`} className="w-40">
            <SelectValue placeholder="Range" />
          </SelectTrigger>
          <SelectContent>
            {DATE_PRESETS.map((p) => (
              <SelectItem key={p.key} value={p.key}>
                {p.label}
              </SelectItem>
            ))}
            <SelectItem value="custom">Custom…</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <Field label="From">
        <Input
          aria-label="From"
          type="date"
          value={value.from || ''}
          onChange={(e) => onChange({ ...value, from: e.target.value || undefined })}
          className="w-40"
        />
      </Field>
      <Field label="To">
        <Input
          aria-label="To"
          type="date"
          // Show the inclusive last day (stored `to` is exclusive).
          value={value.to ? addDayBack(value.to) : ''}
          onChange={(e) => onChange({ ...value, to: e.target.value ? addDay(e.target.value) : undefined })}
          className="w-40"
        />
      </Field>
    </>
  );
}

// Display helper: stored `to` is exclusive; show the day before in the input.
function addDayBack(d: string) {
  const dt = new Date(`${d}T00:00:00Z`);
  dt.setUTCDate(dt.getUTCDate() - 1);
  return dt.toISOString().slice(0, 10);
}

/** A small "Clear filters" button. */
export function ClearFilters({ onClick }: { onClick: () => void }) {
  return (
    <Button variant="ghost" size="sm" className="mb-0.5" onClick={onClick}>
      Clear
    </Button>
  );
}
